import type * as React from "react";
import { useEffect, useMemo, useState } from "react";

// Third Party
import { useNavigate, createFileRoute } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import {
    Card,
    CardHeader,
    CardTitle,
    CardDescription,
    CardContent,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { useToast } from "@/components/ui/use-toast";
import { FileUpload } from "@/components/FileUpload";
import { Spinner } from "@/components/ui/spinner";

// Icons
import { ArrowLeft, Save } from "lucide-react";

// Services
import { getCash, editCashes } from "@/services/finance";
import DinamixSkeletonTable from "@/components/skeleton/DinamixTableSkeleton";

export const Route = createFileRoute(
    "/_layout-private/report/finance/company/_layout/cash-flow-direct-method/edit"
)({
    component: EditCashFlowDirect,
});

function EditCashFlowDirect() {
    const queryClient = useQueryClient();
    const navigate = useNavigate();
    const { toast } = useToast();
    const companyId = localStorage.getItem("companyId");
    const year = Number(localStorage.getItem("year"));
    const theType = "DIRECT";

    const [docFile, setDocFile] = useState("");

    // Initialize state with predefined details
    const [cashDetails, setCashDetails] = useState<
        Array<{
            cashDetailType:
            | "OPERATING_A"
            | "INVESTING_B"
            | "FINANCING_C"
            | "CASH_YEARLY";
            cashDataType: "EXPENSE" | "INCOME";
            name: string;
            amount: number | string;
            remark: string;
            amountForBackend?: number;
        }>
    >([]);

    // Fetch the existing cash details from the backend
    const { data, isLoading } = useQuery({
        queryKey: ["getCashDirect", companyId],
        queryFn: () => getCash(companyId as string, year, theType),
    });

    const calculateTotal = (type: string): number => {
        return cashDetails
            .filter((detail) => detail.cashDetailType === type)
            .reduce((sum, detail) => {
                // Convert the amount to a processable number
                let numericAmount = 0;

                if (detail.amount) {
                    // Remove commas and parentheses
                    const cleanAmount = String(detail.amount).replace(
                        /[,()]/g,
                        ""
                    );
                    numericAmount = Number(cleanAmount);

                    // If original amount was in parentheses (expense), make it negative
                    if (
                        detail.cashDataType === "EXPENSE" ||
                        String(detail.amount).startsWith("(")
                    ) {
                        numericAmount = -Math.abs(numericAmount);
                    }
                }

                return sum + numericAmount;
            }, 0);
    };

    // Update the memoized totals
    const totalOperatingA = useMemo(
        () => calculateTotal("OPERATING_A"),
        [cashDetails]
    );

    const totalInvestingB = useMemo(
        () => calculateTotal("INVESTING_B"),
        [cashDetails]
    );

    const totalFinancingC = useMemo(
        () => calculateTotal("FINANCING_C"),
        [cashDetails]
    );

    // Update the cash yearly calculation
    const totalCashYearly = useMemo(() => {
        const cashYearlyItems = cashDetails.filter(
            (detail) => detail.cashDetailType === "CASH_YEARLY"
        );

        if (cashYearlyItems.length < 2) return 0;

        // Get first and last items
        const firstItem = cashYearlyItems[0];
        const lastItem = cashYearlyItems[cashYearlyItems.length - 1];

        // Convert amounts to numbers
        const getNumericAmount = (detail: any) => {
            if (!detail.amount) return 0;

            // Remove commas and get clean number
            const cleanAmount = String(detail.amount).replace(/[,()]/g, "");
            const numericAmount = Number(cleanAmount);

            // If it's in parentheses or EXPENSE type, it's already negative
            // No need to make it negative again
            return numericAmount;
        };

        const firstAmount = getNumericAmount(firstItem);
        const lastAmount = getNumericAmount(lastItem);

        // Calculate the difference with proper decimal precision
        const difference = Number(
            (lastAmount - Math.abs(firstAmount)).toFixed(2)
        );

        return difference;
    }, [cashDetails]);

    const cashData = data?.data;

    useEffect(() => {
        if (data?.data) {
            // Format initial amounts with proper handling of negative numbers
            const formattedDetails = data.data.cashDetails.map((detail: any) => {
                if (!detail.amount) return detail;

                // Convert to string and remove any existing formatting
                const cleanAmount = String(detail.amount).replace(/[,()]/g, "");
                const numericAmount = Number(cleanAmount);

                // Format the amount based on whether it's an expense or negative number
                const isNegative =
                    numericAmount < 0 || detail.cashDataType === "EXPENSE";

                return {
                    ...detail,
                    amount: isNegative
                        ? `(${Math.abs(numericAmount)})`
                        : String(numericAmount),
                    cashDataType: isNegative ? "EXPENSE" : "INCOME",
                };
            });

            setCashDetails(formattedDetails);
            setDocFile(cashData?.docFile);
        }
    }, [data]);

    // For the CASH_YEARLY total, compute the difference between the last and the first entry.

    const getDetailsByType = (type: string) =>
        cashDetails.filter((detail) => detail.cashDetailType === type);

    const getSectionTitle = (type: string) => {
        const titles: Record<string, string> = {
            OPERATING_A: "ກະແສເງິນສົດລວມທີ່ຕິດພັນກັບການທຸລະກິດ",
            INVESTING_B: "ກະແສເງິນສົດທີ່ຕິດພັນກັບການລົງທຶນ",
            FINANCING_C: "ກະແສເງິນສົດທີ່ຕິດພັນກັບການປະກອບທຶນ",
            CASH_YEARLY: "ກະແສເງິນສົດລວມທີ່ຕິດພັນກັບການທຸລະກິດ",
            // ANNUAL_NET: "ກຳໄລປະຈຳປີ",
        };
        return titles[type];
    };

    // Mutation

    const mutation = useMutation({
        mutationFn: (data: any) => editCashes(companyId as string, data),
        onSuccess: () => {
            toast({
                title: "ສຳເລັດ",
                description: "ບັນທຶກຂໍ້ມູນສຳເລັດ",
                variant: "success",
            });

            localStorage.setItem("year", JSON.stringify(year));
            queryClient.invalidateQueries({
                queryKey: ["getCashDIRECT", companyId, year],
            }); // Fix this line
            navigate({ to: "/report/finance/company/cash-flow-direct-method" });
        },
        onError: (error) => {
            console.log(error, "Error");

            toast({
                title: "ຫຼົ້ມເຫຼວ",
                description: "ບັນທຶກຂໍ້ມູນຫຼົ້ມເຫຼວ",
                variant: "destructive",
            });
        },
    });

    // Functions

    // Fixed updateDetail function
    const updateDetail = (
        type: string,
        typeIndex: number,
        field: string,
        value: string | number
    ) => {
        const newDetails = [...cashDetails];
        const actualIndex = cashDetails.findIndex(
            (detail) =>
                detail.cashDetailType === type &&
                cashDetails
                    .filter((d) => d.cashDetailType === type)
                    .indexOf(detail) === typeIndex
        );

        if (actualIndex !== -1) {
            if (field === "amount") {
                // Remove commas from input
                let processedValue = String(value).replace(/,/g, "");

                // Allow only valid characters (numbers, decimal point, parentheses)
                if (
                    !/^[(]?\d*\.?\d*[)]?$/.test(processedValue) &&
                    processedValue !== ""
                ) {
                    return;
                }

                // Handle decimal places - same logic as add page
                if (processedValue.includes(".")) {
                    const [integerPart, decimalPart] =
                        processedValue.split(".");
                    if (
                        decimalPart &&
                        decimalPart.replace(")", "").length > 2
                    ) {
                        const limitedDecimal = decimalPart
                            .replace(")", "")
                            .slice(0, 2);
                        processedValue = `${integerPart}.${limitedDecimal}${processedValue.endsWith(")") ? ")" : ""}`;
                    }
                }

                // Convert to number for backend
                const numericValue = processedValue.replace(/[()]/g, "");
                const numericAmount = Number(numericValue);

                // FIXED: Store the processedValue directly like in add page
                newDetails[actualIndex] = {
                    ...newDetails[actualIndex],
                    amount: processedValue, // Store raw processed value for display
                    amountForBackend: numericAmount,
                    cashDataType:
                        processedValue.startsWith("(") &&
                            processedValue.endsWith(")")
                            ? "EXPENSE"
                            : "INCOME",
                };
            } else {
                newDetails[actualIndex] = {
                    ...newDetails[actualIndex],
                    [field]: value,
                };
            }
            setCashDetails(newDetails);
        }
    };

    // Fixed formatNumberWithCommas function - same as add page
    const formatNumberWithCommas = (value: string | number) => {
        if (!value && value !== 0) return "";

        // Convert to string and remove commas
        const stringValue = String(value).replace(/,/g, "");

        // Check if the value is wrapped in parentheses
        const isNegative =
            stringValue.startsWith("(") && stringValue.endsWith(")");
        const numericValue = isNegative
            ? stringValue.slice(1, -1)
            : stringValue;

        // Split number into integer and decimal parts
        const [integerPart, decimalPart] = numericValue.split(".");

        // Format integer part with commas
        const formattedInteger = integerPart.replace(
            /\B(?=(\d{3})+(?!\d))/g,
            ","
        );

        // Combine with decimal part if it exists (DON'T slice it here)
        const formattedValue =
            decimalPart !== undefined
                ? `${formattedInteger}.${decimalPart}`
                : formattedInteger;

        return isNegative ? `(${formattedValue})` : formattedValue;
    };

    // Helper functions
    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!companyId) return;

        if (!docFile) {
            toast({
                title: "ບັນທຶກລົ້ມເຫຼວ",
                description: "ກະລຸນາອັບໂຫຼດເອກະສານຕິດຂັດ",
                variant: "destructive",
            });
            return;
        }

        console.log(cashDetails, "Cash Details");

        const modifiedFinancialDetails = cashDetails.map(
            ({ amountForBackend, amount, ...rest }) => ({
                ...rest,
                amount:
                    amountForBackend ||
                    Number(amount.toString().replace(/[()]/g, "")) ||
                    0,
            })
        );

        // Check if 'amount' is invalid (NaN, null, or undefined) in any item
        const hasInvalidAmount = modifiedFinancialDetails.some(({ amount }) => {
            // Convert the amount to a number and check for NaN
            const numericAmount = Number(amount);
            return (
                Number.isNaN(numericAmount) ||
                amount === null ||
                amount === undefined
            );
        });

        if (hasInvalidAmount) {
            // If there's an invalid amount, just return/stop the function

            toast({
                title: "ກະລຸນາປ້ອນຂໍ້ມູນໃຫ້ຖືກຕ້ອງ",
                description: "ກະລຸນາກວດສອບການປ້ອນຈຳນວນຕົວເລກໃນຟອມ",
                variant: "destructive",
            });

            return;
        }

        const data = {
            companyId,
            yearly: year,
            docFile,
            cashType: "DIRECT",
            cashDetails: modifiedFinancialDetails,
        };

        console.log("Data", data);

        mutation.mutate(data);
    };

    return (
        <section>
            <Card className="border-none">
                <CardHeader className="border-b mb-[32px]">
                    <button
                        type="button"
                        onClick={() =>
                            navigate({
                                to: "/report/finance/company/cash-flow-direct-method",
                            })
                        }
                        className="flex items-center gap-[4px] mb-4"
                    >
                        <ArrowLeft className="w-5 h-5" />
                        ກັບຄືນ
                    </button>
                    <CardTitle>ແກ້ໄຂຂໍ້ມູນກະແສເງິນສົດ (ວິທີທາງກົງ)</CardTitle>
                    <CardDescription>
                        ກະລຸນາປ້ອນຂໍ້ມູນໃຫ້ຄົບຖ້ວນ
                    </CardDescription>
                </CardHeader>
                {isLoading ? (
                    <DinamixSkeletonTable />
                ) : (
                    <CardContent>
                        <form onSubmit={handleSubmit}>
                            <Table className="border">
                                <TableHeader>
                                    <TableRow className="[&>*]:border [&>*]:font-bold [&>*]:text-black">
                                        <TableHead>ເນື້ອໃນລາຍງານ</TableHead>
                                        <TableHead>
                                            ມູນຄ່າຍັງເຫຼືອໃນປີນີ້{" "}
                                            <span className="text-red-500">
                                                *
                                            </span>
                                        </TableHead>
                                        <TableHead>ໝາຍເຫດ</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {/*TODO: ກະແສເງິນສົດລວມທີ່ຕິດພັນກັບການທຸລະກິດ A */}
                                    <TableRow className="bg-[#216C4C]/20 [&>*]:font-bold [&>*]:text-black">
                                        <TableCell colSpan={4}>
                                            {getSectionTitle("OPERATING_A")}
                                        </TableCell>
                                    </TableRow>

                                    {getDetailsByType("OPERATING_A").map(
                                        (detail, index) => (
                                            <TableRow
                                                key={index}
                                                className="[&>*]:border"
                                            >
                                                <TableCell className="flex gap-x-1 items-center">
                                                    {index + 1}.
                                                    <Input
                                                        value={detail.name}
                                                        onChange={(e) =>
                                                            updateDetail(
                                                                "OPERATING_A",
                                                                index,
                                                                "name",
                                                                e.target.value
                                                            )
                                                        }
                                                        readOnly={index < 6}
                                                    // disabled={[0, 1, 2, 3, 4, 5].includes(index)}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <Input
                                                        type="text"
                                                        value={formatNumberWithCommas(
                                                            detail.amount
                                                        )}
                                                        onChange={(e) => {
                                                            const value =
                                                                e.target.value;
                                                            // Allow only numbers, decimal point, parentheses, and commas
                                                            if (
                                                                /^[(]?\d*\.?\d*[)]?$/.test(
                                                                    value.replace(
                                                                        /,/g,
                                                                        ""
                                                                    )
                                                                ) ||
                                                                value === ""
                                                            ) {
                                                                updateDetail(
                                                                    detail.cashDetailType,
                                                                    index,
                                                                    "amount",
                                                                    value
                                                                );
                                                            }
                                                        }}
                                                        className="text-right"
                                                        inputMode="numeric"
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <Input
                                                        value={detail.remark}
                                                        onChange={(e) =>
                                                            updateDetail(
                                                                "OPERATING_A",
                                                                index,
                                                                "remark",
                                                                e.target.value
                                                            )
                                                        }
                                                    />
                                                </TableCell>
                                            </TableRow>
                                        )
                                    )}

                                    <TableRow className="bg-gray-100 [&>*]:font-bold [&>*]:text-black">
                                        <TableCell>
                                            ກະແສເງິນສົດລວມ
                                            ທີ່ຕິດພັນກັບການທຸລະກິດ (ກ)
                                        </TableCell>
                                        <TableCell className="text-right">
                                            {formatNumberWithCommas(
                                                totalOperatingA
                                            )}
                                        </TableCell>
                                        <TableCell>-</TableCell>
                                        <TableCell> </TableCell>
                                    </TableRow>

                                    {/* TODO: ກະແສເງິນສົດລວມສຸດທິ ຈາກການລົງທຶນ (ຂ) */}
                                    <TableRow className="bg-[#216C4C]/20 [&>*]:font-bold [&>*]:text-black">
                                        <TableCell colSpan={4}>
                                            {getSectionTitle("INVESTING_B")}
                                        </TableCell>
                                    </TableRow>

                                    {getDetailsByType("INVESTING_B").map(
                                        (detail, index) => (
                                            <TableRow
                                                key={index}
                                                className="[&>*]:border"
                                            >
                                                <TableCell className="flex gap-x-1 items-center">
                                                    {index + 1}.
                                                    <Input
                                                        value={detail.name}
                                                        onChange={(e) =>
                                                            updateDetail(
                                                                "INVESTING_B",
                                                                index,
                                                                "name",
                                                                e.target.value
                                                            )
                                                        }
                                                        readOnly={index < 3}
                                                    // disabled={[0, 1, 2, 3, 4, 5].includes(index)}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <Input
                                                        type="text"
                                                        value={formatNumberWithCommas(
                                                            detail.amount
                                                        )}
                                                        onChange={(e) => {
                                                            const value =
                                                                e.target.value;
                                                            // Allow only numbers, decimal point, parentheses, and commas
                                                            if (
                                                                /^[(]?\d*\.?\d*[)]?$/.test(
                                                                    value.replace(
                                                                        /,/g,
                                                                        ""
                                                                    )
                                                                ) ||
                                                                value === ""
                                                            ) {
                                                                updateDetail(
                                                                    detail.cashDetailType,
                                                                    index,
                                                                    "amount",
                                                                    value
                                                                );
                                                            }
                                                        }}
                                                        className="text-right"
                                                        inputMode="numeric"
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <Input
                                                        value={detail.remark}
                                                        onChange={(e) =>
                                                            updateDetail(
                                                                "INVESTING_B",
                                                                index,
                                                                "remark",
                                                                e.target.value
                                                            )
                                                        }
                                                    />
                                                </TableCell>
                                            </TableRow>
                                        )
                                    )}

                                    <TableRow className="bg-gray-100 [&>*]:font-bold [&>*]:text-black">
                                        <TableCell>
                                            ກະແສເງິນສົດລວມສຸດທິ ຈາກການລົງທຶນ (ຂ)
                                        </TableCell>
                                        <TableCell className="text-right">
                                            {formatNumberWithCommas(
                                                totalInvestingB
                                            )}
                                        </TableCell>
                                        <TableCell>-</TableCell>
                                        <TableCell> </TableCell>
                                    </TableRow>

                                    {/* TODO: ກະແສເງິນສົດລວມສຸດທິ ຈາກການປະກອບທຶນ ( ຄ ) */}
                                    <TableRow className="bg-[#216C4C]/20 [&>*]:font-bold [&>*]:text-black">
                                        <TableCell colSpan={4}>
                                            {getSectionTitle("FINANCING_C")}
                                        </TableCell>
                                    </TableRow>

                                    {getDetailsByType("FINANCING_C").map(
                                        (detail, index) => (
                                            <TableRow
                                                key={index}
                                                className="[&>*]:border"
                                            >
                                                <TableCell className="flex gap-x-1 items-center">
                                                    {index + 1}.
                                                    <Input
                                                        value={detail.name}
                                                        onChange={(e) =>
                                                            updateDetail(
                                                                "FINANCING_C",
                                                                index,
                                                                "name",
                                                                e.target.value
                                                            )
                                                        }
                                                        readOnly={index < 3}
                                                    // disabled={[0, 1, 2, 3, 4, 5].includes(index)}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <Input
                                                        type="text"
                                                        value={formatNumberWithCommas(
                                                            detail.amount
                                                        )}
                                                        onChange={(e) => {
                                                            const value =
                                                                e.target.value;
                                                            // Allow only numbers, decimal point, parentheses, and commas
                                                            if (
                                                                /^[(]?\d*\.?\d*[)]?$/.test(
                                                                    value.replace(
                                                                        /,/g,
                                                                        ""
                                                                    )
                                                                ) ||
                                                                value === ""
                                                            ) {
                                                                updateDetail(
                                                                    detail.cashDetailType,
                                                                    index,
                                                                    "amount",
                                                                    value
                                                                );
                                                            }
                                                        }}
                                                        className="text-right"
                                                        inputMode="numeric"
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <Input
                                                        value={detail.remark}
                                                        onChange={(e) =>
                                                            updateDetail(
                                                                "FINANCING_C",
                                                                index,
                                                                "remark",
                                                                e.target.value
                                                            )
                                                        }
                                                    />
                                                </TableCell>
                                            </TableRow>
                                        )
                                    )}

                                    <TableRow className="bg-gray-100 [&>*]:font-bold [&>*]:text-black">
                                        <TableCell>
                                            ກະແສເງິນສົດລວມສຸດທິ ຈາກການປະກອບທຶນ
                                            (ຄ)
                                        </TableCell>
                                        <TableCell className="text-right">
                                            {formatNumberWithCommas(
                                                totalFinancingC
                                            )}
                                        </TableCell>
                                        <TableCell>-</TableCell>
                                        <TableCell> </TableCell>
                                    </TableRow>
                                    <TableRow className="bg-gray-100 [&>*]:font-bold [&>*]:text-black">
                                        <TableCell>
                                            ສ່ວນປ່ຽນແປງກະແສເງິນສົດລວມໃນປີ ( ກ +
                                            ຂ + ຄ )
                                        </TableCell>
                                        <TableCell className="text-right">
                                            {formatNumberWithCommas(
                                                totalOperatingA +
                                                totalInvestingB +
                                                totalFinancingC
                                            )}
                                        </TableCell>
                                        <TableCell>-</TableCell>
                                        <TableCell> </TableCell>
                                    </TableRow>

                                    {/* TODO: CASH YEARLY */}
                                    {getDetailsByType("CASH_YEARLY").map(
                                        (detail, index) => (
                                            <TableRow
                                                key={index}
                                                className="[&>*]:border"
                                            >
                                                <TableCell>
                                                    <p>{detail.name}</p>
                                                </TableCell>
                                                <TableCell>
                                                    <Input
                                                        type="text"
                                                        value={formatNumberWithCommas(
                                                            detail.amount
                                                        )}
                                                        onChange={(e) => {
                                                            const value =
                                                                e.target.value;
                                                            // Allow only numbers, decimal point, parentheses, and commas
                                                            if (
                                                                /^[(]?\d*\.?\d*[)]?$/.test(
                                                                    value.replace(
                                                                        /,/g,
                                                                        ""
                                                                    )
                                                                ) ||
                                                                value === ""
                                                            ) {
                                                                updateDetail(
                                                                    detail.cashDetailType,
                                                                    index,
                                                                    "amount",
                                                                    value
                                                                );
                                                            }
                                                        }}
                                                        className="text-right"
                                                        inputMode="numeric"
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <Input
                                                        value={detail.remark}
                                                        onChange={(e) =>
                                                            updateDetail(
                                                                "CASH_YEARLY",
                                                                index,
                                                                "remark",
                                                                e.target.value
                                                            )
                                                        }
                                                    />
                                                </TableCell>
                                            </TableRow>
                                        )
                                    )}

                                    <TableRow className="bg-gray-100 [&>*]:font-bold [&>*]:text-black">
                                        <TableCell className="text-center">
                                            ສ່ວນປ່ຽນແປງ ເງິນສົດລວມ
                                        </TableCell>
                                        <TableCell className="text-right">
                                            {formatNumberWithCommas(
                                                totalCashYearly
                                            )}
                                        </TableCell>
                                        <TableCell>-</TableCell>
                                        <TableCell> </TableCell>
                                    </TableRow>
                                </TableBody>
                            </Table>

                            {/* File Input */}
                            <div className="flex gap-x-1 items-center mt-4">
                                <FileUpload
                                    currentFile={docFile}
                                    onFileUpload={(file) => {
                                        console.log(file, "File Uploaded");
                                        setDocFile(file);
                                    }}
                                />
                            </div>

                            {/* Submit Buttons */}
                            <div className="flex gap-x-2 justify-end mt-4">
                                <Button
                                    type="button"
                                    onClick={() =>
                                        navigate({
                                            to: "/report/finance",
                                            search: {
                                                page: 1,
                                                startYear: 0,
                                                endYear: 0,
                                            },
                                        })
                                    }
                                    variant={"outline"}
                                >
                                    ຍົກເລີກ
                                </Button>
                                <Button
                                    disabled={mutation.isPending}
                                    type="submit"
                                    className="flex gap-x-2 items-center"
                                >
                                    {mutation.isPending ? (
                                        <Spinner className="text-white" />
                                    ) : (
                                        <>
                                            <Save className="w-4 h-4" />
                                            ບັນທຶກ
                                        </>
                                    )}
                                </Button>
                            </div>
                        </form>
                    </CardContent>
                )}
            </Card>
        </section>
    );
}

export default EditCashFlowDirect;
